import { useState, useMemo } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Search, FileCheck, Globe, Stamp, Plane, FileText, CheckCircle2, Circle,
    ChevronDown, ChevronUp, User, Phone, Calendar, BookOpen,
    Download, Eye, X, AlertCircle
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/context/auth-context'
import { mockAbroadLeads, mockPartnerLeads } from '@/data/admin-mock-data'
import { toast } from 'sonner'

interface ChecklistItem {
    id: string
    label: string
    icon: any
    completed: boolean
    date?: string
    notes?: string
}

interface AdmittedStudent {
    id: string
    name: string
    email: string
    phone: string
    type: 'Abroad' | 'Partner'
    source: string
    admittedDate: string
    checklist: ChecklistItem[]
    emergencyContact?: string
    passportNumber?: string
    visaStatus?: 'not-applied' | 'applied' | 'interview-scheduled' | 'approved' | 'rejected'
    travelDate?: string
    travelStatus?: 'not-booked' | 'booked' | 'confirmed' | 'departed' | 'arrived'
    universityName?: string
    courseName?: string
    notes?: string
}

const createDefaultChecklist = (): ChecklistItem[] => [
    { id: 'passport', label: 'Passport Verification', icon: Stamp, completed: false },
    { id: 'documents', label: 'Documents Uploaded', icon: FileText, completed: false },
    { id: 'university', label: 'University Applied', icon: Globe, completed: false },
    { id: 'offer-letter', label: 'Offer Letter Received', icon: FileCheck, completed: false },
    { id: 'visa', label: 'Visa Processing', icon: FileCheck, completed: false },
    { id: 'travel', label: 'Travel Arranged', icon: Plane, completed: false },
]

export function CounsellorAdmissionTrackerView() {
    const { user } = useAuth()

    // Get admitted leads for this counsellor
    const myAbroadLeads = mockAbroadLeads.filter(l => l.assignedMentorId === user?.id && (l.status as string) === 'admitted')
    const myPartnerLeads = mockPartnerLeads.filter(l => l.assignedMentorId === user?.id && (l.status as string) === 'admitted')

    const initialStudents: AdmittedStudent[] = [
        ...myAbroadLeads.map(l => ({
            id: l.id,
            name: l.studentName,
            email: l.email,
            phone: l.phone,
            type: 'Abroad' as const,
            source: l.collegeName,
            admittedDate: l.createdAt,
            checklist: createDefaultChecklist(),
            visaStatus: 'not-applied' as const,
            travelStatus: 'not-booked' as const,
            universityName: l.collegeName,
        })),
        ...myPartnerLeads.map(l => ({
            id: l.id,
            name: l.studentName,
            email: l.email,
            phone: l.phone,
            type: 'Partner' as const,
            source: l.instituteName,
            admittedDate: l.createdAt,
            checklist: createDefaultChecklist(),
            visaStatus: 'not-applied' as const,
            travelStatus: 'not-booked' as const,
        })),
        // Add demo admitted students for showcase
        {
            id: 'demo-1',
            name: 'Priya Sharma',
            email: 'priya.sharma@gmail.com',
            phone: '+91-9876543210',
            type: 'Abroad',
            source: 'University of Melbourne',
            admittedDate: '2025-02-15',
            checklist: [
                { id: 'passport', label: 'Passport Verification', icon: Stamp, completed: true, date: '2025-02-18' },
                { id: 'documents', label: 'Documents Uploaded', icon: FileText, completed: true, date: '2025-02-20' },
                { id: 'university', label: 'University Applied', icon: Globe, completed: true, date: '2025-02-25' },
                { id: 'offer-letter', label: 'Offer Letter Received', icon: FileCheck, completed: true, date: '2025-03-10' },
                { id: 'visa', label: 'Visa Processing', icon: FileCheck, completed: true, date: '2025-03-20' },
                { id: 'travel', label: 'Travel Arranged', icon: Plane, completed: false },
            ],
            visaStatus: 'approved',
            travelStatus: 'booked',
            universityName: 'University of Melbourne',
            courseName: 'MBBS - International',
            passportNumber: 'N1234567',
            travelDate: '2025-07-15',
        },
        {
            id: 'demo-2',
            name: 'Rahul Verma',
            email: 'rahul.verma@gmail.com',
            phone: '+91-9123456789',
            type: 'Abroad',
            source: 'Kazan Federal University',
            admittedDate: '2025-03-01',
            checklist: [
                { id: 'passport', label: 'Passport Verification', icon: Stamp, completed: true, date: '2025-03-05' },
                { id: 'documents', label: 'Documents Uploaded', icon: FileText, completed: true, date: '2025-03-08' },
                { id: 'university', label: 'University Applied', icon: Globe, completed: true, date: '2025-03-12' },
                { id: 'offer-letter', label: 'Offer Letter Received', icon: FileCheck, completed: false },
                { id: 'visa', label: 'Visa Processing', icon: FileCheck, completed: false },
                { id: 'travel', label: 'Travel Arranged', icon: Plane, completed: false },
            ],
            visaStatus: 'not-applied',
            travelStatus: 'not-booked',
            universityName: 'Kazan Federal University',
            courseName: 'MBBS',
            passportNumber: 'P9876543',
        },
        {
            id: 'demo-3',
            name: 'Ananya Patel',
            email: 'ananya.p@gmail.com',
            phone: '+91-9988776655',
            type: 'Partner',
            source: 'Allen Career Institute',
            admittedDate: '2025-03-10',
            checklist: [
                { id: 'passport', label: 'Passport Verification', icon: Stamp, completed: true, date: '2025-03-12' },
                { id: 'documents', label: 'Documents Uploaded', icon: FileText, completed: true, date: '2025-03-14' },
                { id: 'university', label: 'University Applied', icon: Globe, completed: false },
                { id: 'offer-letter', label: 'Offer Letter Received', icon: FileCheck, completed: false },
                { id: 'visa', label: 'Visa Processing', icon: FileCheck, completed: false },
                { id: 'travel', label: 'Travel Arranged', icon: Plane, completed: false },
            ],
            visaStatus: 'not-applied',
            travelStatus: 'not-booked',
        },
    ]

    const [students, setStudents] = useState<AdmittedStudent[]>(initialStudents)
    const [searchQuery, setSearchQuery] = useState('')
    const [typeFilter, setTypeFilter] = useState<'all' | 'Abroad' | 'Partner'>('all')
    const [selectedStudent, setSelectedStudent] = useState<AdmittedStudent | null>(null)
    const [expandedId, setExpandedId] = useState<string | null>(null)

    const filteredStudents = useMemo(() => {
        return students.filter(s => {
            const matchSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.email.toLowerCase().includes(searchQuery.toLowerCase())
            const matchType = typeFilter === 'all' || s.type === typeFilter
            return matchSearch && matchType
        })
    }, [students, searchQuery, typeFilter])

    const stats = useMemo(() => ({
        total: students.length,
        passportDone: students.filter(s => s.checklist.find(c => c.id === 'passport')?.completed).length,
        offerReceived: students.filter(s => s.checklist.find(c => c.id === 'offer-letter')?.completed).length,
        visaApproved: students.filter(s => s.visaStatus === 'approved').length,
        travelReady: students.filter(s => s.travelStatus === 'booked' || s.travelStatus === 'confirmed').length,
        fullyComplete: students.filter(s => s.checklist.every(c => c.completed)).length,
    }), [students])

    const toggleChecklistItem = (studentId: string, itemId: string) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== studentId) return s
            return {
                ...s,
                checklist: s.checklist.map(c =>
                    c.id === itemId ? { ...c, completed: !c.completed, date: !c.completed ? new Date().toISOString().split('T')[0] : undefined } : c
                )
            }
        }))
        // Also update selected student if viewing
        if (selectedStudent?.id === studentId) {
            setSelectedStudent(prev => {
                if (!prev) return null
                return {
                    ...prev,
                    checklist: prev.checklist.map(c =>
                        c.id === itemId ? { ...c, completed: !c.completed, date: !c.completed ? new Date().toISOString().split('T')[0] : undefined } : c
                    )
                }
            })
        }
        toast.success('Checklist updated')
    }

    const getCompletionPercent = (checklist: ChecklistItem[]) => {
        const done = checklist.filter(c => c.completed).length
        return Math.round((done / checklist.length) * 100)
    }

    const getVisaStatusBadge = (status?: string) => {
        switch (status) {
            case 'not-applied': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">Not Applied</span>
            case 'applied': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">Applied</span>
            case 'interview-scheduled': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Interview Scheduled</span>
            case 'approved': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Approved ✅</span>
            case 'rejected': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Rejected</span>
            default: return null
        }
    }

    const getTravelStatusBadge = (status?: string) => {
        switch (status) {
            case 'not-booked': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">Not Booked</span>
            case 'booked': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">Booked</span>
            case 'confirmed': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Confirmed</span>
            case 'departed': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">Departed</span>
            case 'arrived': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">Arrived ✅</span>
            default: return null
        }
    }

    const updateVisaStatus = (studentId: string, status: string) => {
        setStudents(prev => prev.map(s => s.id === studentId ? { ...s, visaStatus: status as any } : s))
        if (selectedStudent?.id === studentId) {
            setSelectedStudent(prev => prev ? { ...prev, visaStatus: status as any } : null)
        }
        toast.success('Visa status updated')
    }

    const updateTravelStatus = (studentId: string, status: string) => {
        setStudents(prev => prev.map(s => s.id === studentId ? { ...s, travelStatus: status as any } : s))
        if (selectedStudent?.id === studentId) {
            setSelectedStudent(prev => prev ? { ...prev, travelStatus: status as any } : null)
        }
        toast.success('Travel status updated')
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div>
                <h2 className="text-2xl font-bold text-theme-primary">Admission Tracker</h2>
                <p className="text-sm text-theme-secondary">Track onboarding progress for admitted students</p>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <GlassmorphicCard className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-2">
                        <User className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{stats.total}</p>
                    <p className="text-xs text-theme-secondary">Total Admitted</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-2">
                        <Stamp className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{stats.passportDone}</p>
                    <p className="text-xs text-theme-secondary">Passport Done</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mb-2">
                        <FileCheck className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{stats.offerReceived}</p>
                    <p className="text-xs text-theme-secondary">Offer Letters</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mb-2">
                        <Globe className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{stats.visaApproved}</p>
                    <p className="text-xs text-theme-secondary">Visa Approved</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center mb-2">
                        <Plane className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{stats.travelReady}</p>
                    <p className="text-xs text-theme-secondary">Travel Ready</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-2">
                        <CheckCircle2 className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{stats.fullyComplete}</p>
                    <p className="text-xs text-theme-secondary">Fully Complete</p>
                </GlassmorphicCard>
            </div>

            {/* Search & Filters */}
            <GlassmorphicCard className="p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-secondary" />
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                        />
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value as any)}
                            className="px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                        >
                            <option value="all">All Types</option>
                            <option value="Abroad">Abroad</option>
                            <option value="Partner">Partner</option>
                        </select>
                        <button className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-secondary rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                            <Download className="h-4 w-4" />
                            <span className="text-sm font-medium hidden sm:inline">Export</span>
                        </button>
                    </div>
                </div>
            </GlassmorphicCard>

            {/* Student Cards */}
            <div className="space-y-4">
                {filteredStudents.map((student) => {
                    const percent = getCompletionPercent(student.checklist)
                    const isExpanded = expandedId === student.id

                    return (
                        <GlassmorphicCard key={student.id} className="overflow-hidden">
                            {/* Card Header */}
                            <div
                                className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                                onClick={() => setExpandedId(isExpanded ? null : student.id)}
                            >
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-blue to-violet-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                                    {student.name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-bold text-theme-primary truncate">{student.name}</h3>
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${student.type === 'Abroad' ? 'bg-indigo-100 text-indigo-700' : 'bg-orange-100 text-orange-700'}`}>
                                            {student.type}
                                        </span>
                                    </div>
                                    <p className="text-xs text-theme-secondary truncate">{student.source} · {student.email}</p>
                                </div>

                                {/* Progress Ring */}
                                <div className="hidden sm:flex items-center gap-3 shrink-0">
                                    <div className="relative w-12 h-12">
                                        <svg className="w-12 h-12 transform -rotate-90">
                                            <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="none" className="text-slate-100 dark:text-slate-800" />
                                            <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="none"
                                                className={`${percent === 100 ? 'text-emerald-500' : 'text-primary-blue'}`}
                                                strokeDasharray={`${2 * Math.PI * 20}`}
                                                strokeDashoffset={`${2 * Math.PI * 20 * (1 - percent / 100)}`}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-theme-primary">{percent}%</span>
                                    </div>
                                </div>

                                <button className="p-2 text-theme-secondary shrink-0">
                                    {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                </button>
                            </div>

                            {/* Mobile progress bar */}
                            <div className="px-4 pb-2 sm:hidden">
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full transition-all ${percent === 100 ? 'bg-emerald-500' : 'bg-primary-blue'}`} style={{ width: `${percent}%` }} />
                                    </div>
                                    <span className="text-xs font-bold text-theme-primary">{percent}%</span>
                                </div>
                            </div>

                            {/* Expanded Checklist */}
                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-800 pt-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Checklist */}
                                                <div className="space-y-2">
                                                    <h4 className="text-sm font-semibold text-theme-primary mb-3">Onboarding Checklist</h4>
                                                    {student.checklist.map((item) => {
                                                        const Icon = item.icon
                                                        return (
                                                            <button
                                                                key={item.id}
                                                                onClick={(e) => { e.stopPropagation(); toggleChecklistItem(student.id, item.id) }}
                                                                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${item.completed
                                                                    ? 'bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30'
                                                                    : 'bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:border-primary-blue/30'
                                                                    }`}
                                                            >
                                                                {item.completed
                                                                    ? <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                                                                    : <Circle className="h-5 w-5 text-slate-300 dark:text-slate-600 shrink-0" />
                                                                }
                                                                <Icon className={`h-4 w-4 shrink-0 ${item.completed ? 'text-green-600' : 'text-theme-secondary'}`} />
                                                                <div className="flex-1 min-w-0">
                                                                    <span className={`text-sm font-medium ${item.completed ? 'text-green-700 dark:text-green-400 line-through' : 'text-theme-primary'}`}>
                                                                        {item.label}
                                                                    </span>
                                                                    {item.date && (
                                                                        <p className="text-xs text-green-600 mt-0.5">{new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        )
                                                    })}
                                                </div>

                                                {/* Status & Details */}
                                                <div className="space-y-4">
                                                    {/* Visa Status */}
                                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                        <h5 className="text-sm font-semibold text-theme-primary mb-2 flex items-center gap-2">
                                                            <Stamp className="h-4 w-4 text-indigo-500" /> Visa Status
                                                        </h5>
                                                        <select
                                                            value={student.visaStatus}
                                                            onChange={(e) => updateVisaStatus(student.id, e.target.value)}
                                                            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-theme-primary"
                                                        >
                                                            <option value="not-applied">Not Applied</option>
                                                            <option value="applied">Applied</option>
                                                            <option value="interview-scheduled">Interview Scheduled</option>
                                                            <option value="approved">Approved</option>
                                                            <option value="rejected">Rejected</option>
                                                        </select>
                                                        <div className="mt-2">{getVisaStatusBadge(student.visaStatus)}</div>
                                                    </div>

                                                    {/* Travel Status */}
                                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                        <h5 className="text-sm font-semibold text-theme-primary mb-2 flex items-center gap-2">
                                                            <Plane className="h-4 w-4 text-sky-500" /> Travel Status
                                                        </h5>
                                                        <select
                                                            value={student.travelStatus}
                                                            onChange={(e) => updateTravelStatus(student.id, e.target.value)}
                                                            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-theme-primary"
                                                        >
                                                            <option value="not-booked">Not Booked</option>
                                                            <option value="booked">Booked</option>
                                                            <option value="confirmed">Confirmed</option>
                                                            <option value="departed">Departed</option>
                                                            <option value="arrived">Arrived</option>
                                                        </select>
                                                        <div className="mt-2">{getTravelStatusBadge(student.travelStatus)}</div>
                                                        {student.travelDate && (
                                                            <p className="text-xs text-theme-secondary mt-2 flex items-center gap-1">
                                                                <Calendar className="h-3 w-3" /> Travel Date: {new Date(student.travelDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Quick Info */}
                                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl space-y-2">
                                                        {student.universityName && (
                                                            <div className="flex items-center gap-2 text-sm">
                                                                <Globe className="h-4 w-4 text-theme-secondary shrink-0" />
                                                                <span className="text-theme-primary">{student.universityName}</span>
                                                            </div>
                                                        )}
                                                        {student.courseName && (
                                                            <div className="flex items-center gap-2 text-sm">
                                                                <BookOpen className="h-4 w-4 text-theme-secondary shrink-0" />
                                                                <span className="text-theme-primary">{student.courseName}</span>
                                                            </div>
                                                        )}
                                                        {student.passportNumber && (
                                                            <div className="flex items-center gap-2 text-sm">
                                                                <Stamp className="h-4 w-4 text-theme-secondary shrink-0" />
                                                                <span className="text-theme-primary">Passport: {student.passportNumber}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <Phone className="h-4 w-4 text-theme-secondary shrink-0" />
                                                            <span className="text-theme-primary">{student.phone}</span>
                                                        </div>
                                                    </div>

                                                    {/* View Full Details */}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setSelectedStudent(student) }}
                                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-blue/10 text-primary-blue hover:bg-primary-blue/20 rounded-xl text-sm font-medium transition-colors"
                                                    >
                                                        <Eye className="h-4 w-4" /> View Full Details
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </GlassmorphicCard>
                    )
                })}
            </div>

            {filteredStudents.length === 0 && (
                <div className="text-center py-16">
                    <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-theme-secondary font-medium">No admitted students found</p>
                    <p className="text-sm text-theme-muted mt-1">Students will appear here once marked as Admitted in your leads.</p>
                </div>
            )}

            {/* Full Detail Modal */}
            <AnimatePresence>
                {selectedStudent && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedStudent(null)}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            onClick={e => e.stopPropagation()}
                            className="w-full max-w-xl"
                        >
                            <GlassmorphicCard className="p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-blue to-violet-600 flex items-center justify-center text-white font-bold text-xl">
                                            {selectedStudent.name.charAt(0)}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-theme-primary">{selectedStudent.name}</h3>
                                            <p className="text-sm text-theme-secondary">{selectedStudent.email}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${selectedStudent.type === 'Abroad' ? 'bg-indigo-100 text-indigo-700' : 'bg-orange-100 text-orange-700'}`}>
                                                    {selectedStudent.type}
                                                </span>
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-green-100 text-green-700">
                                                    Admitted
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <button onClick={() => setSelectedStudent(null)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                        <X className="h-5 w-5 text-theme-secondary" />
                                    </button>
                                </div>

                                {/* Full Progress */}
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-theme-primary">Overall Progress</span>
                                        <span className="text-sm font-bold text-primary-blue">{getCompletionPercent(selectedStudent.checklist)}%</span>
                                    </div>
                                    <div className="h-3 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all ${getCompletionPercent(selectedStudent.checklist) === 100 ? 'bg-emerald-500' : 'bg-primary-blue'}`}
                                            style={{ width: `${getCompletionPercent(selectedStudent.checklist)}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Contact Details */}
                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                        <p className="text-xs text-theme-secondary flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</p>
                                        <p className="text-sm font-medium text-theme-primary mt-1">{selectedStudent.phone}</p>
                                    </div>
                                    <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                        <p className="text-xs text-theme-secondary flex items-center gap-1"><Calendar className="h-3 w-3" /> Admitted On</p>
                                        <p className="text-sm font-medium text-theme-primary mt-1">{new Date(selectedStudent.admittedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                    </div>
                                    {selectedStudent.universityName && (
                                        <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                            <p className="text-xs text-theme-secondary flex items-center gap-1"><Globe className="h-3 w-3" /> University</p>
                                            <p className="text-sm font-medium text-theme-primary mt-1">{selectedStudent.universityName}</p>
                                        </div>
                                    )}
                                    {selectedStudent.passportNumber && (
                                        <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                            <p className="text-xs text-theme-secondary flex items-center gap-1"><Stamp className="h-3 w-3" /> Passport</p>
                                            <p className="text-sm font-medium text-theme-primary mt-1">{selectedStudent.passportNumber}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Status Badges */}
                                <div className="flex flex-wrap gap-3 mb-6">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-theme-secondary">Visa:</span>
                                        {getVisaStatusBadge(selectedStudent.visaStatus)}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-theme-secondary">Travel:</span>
                                        {getTravelStatusBadge(selectedStudent.travelStatus)}
                                    </div>
                                    {selectedStudent.travelDate && (
                                        <div className="flex items-center gap-1.5 text-xs text-theme-secondary">
                                            <Plane className="h-3.5 w-3.5" />
                                            {new Date(selectedStudent.travelDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </div>
                                    )}
                                </div>

                                {/* Checklist */}
                                <h4 className="text-sm font-bold text-theme-primary mb-3">Checklist Breakdown</h4>
                                <div className="space-y-2">
                                    {selectedStudent.checklist.map((item) => {
                                        const Icon = item.icon
                                        return (
                                            <div
                                                key={item.id}
                                                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${item.completed
                                                    ? 'bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30'
                                                    : 'bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700'
                                                    }`}
                                            >
                                                {item.completed
                                                    ? <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                                                    : <Circle className="h-5 w-5 text-slate-300 dark:text-slate-600 shrink-0" />
                                                }
                                                <Icon className={`h-4 w-4 shrink-0 ${item.completed ? 'text-green-600' : 'text-theme-secondary'}`} />
                                                <span className={`text-sm font-medium flex-1 ${item.completed ? 'text-green-700 dark:text-green-400' : 'text-theme-primary'}`}>
                                                    {item.label}
                                                </span>
                                                {item.date && (
                                                    <span className="text-xs text-green-600 shrink-0">
                                                        {new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                    </span>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    )
}
